import { writeFile, access } from 'fs/promises';
import { join } from 'path';
import chalk from 'chalk';
import { confirm, select, success, info, tip } from '../utils/prompt.js';
import { getDefaultConfig } from '../utils/config-loader.js';
import { InitOptions } from '../types/index.js';

/**
 * Initialize mem2 configuration in current directory
 */
export async function initCommand(options: InitOptions): Promise<void> {
  const cwd = process.cwd();
  const configPath = join(cwd, '.mem2rc.json');

  console.log(chalk.bold('\n🚀 Initialize mem2 configuration\n'));

  // Check if config already exists
  const configExists = await fileExists(configPath);

  if (configExists && !options.force) {
    const shouldOverwrite = await confirm(
      '.mem2rc.json already exists. Overwrite?',
      false
    );
    if (!shouldOverwrite) {
      info('Initialization cancelled');
      return;
    }
  }

  // Create .mem2rc.json
  const config = getDefaultConfig();
  await writeFile(configPath, JSON.stringify(config, null, 2), 'utf-8');
  success('Created .mem2rc.json');

  // Check for existing memory files
  const hasMemory = await fileExists(join(cwd, 'MEMORY.md'));
  const hasClaude = await fileExists(join(cwd, 'CLAUDE.md'));
  const hasAgents = await fileExists(join(cwd, 'AGENTS.md'));

  if (hasClaude || hasAgents) {
    console.log();
    info('Existing memory files detected:');
    if (hasClaude) console.log(chalk.dim('  - CLAUDE.md'));
    if (hasAgents) console.log(chalk.dim('  - AGENTS.md'));

    if (!hasMemory) {
      console.log();
      const shouldImport = await confirm(
        'Would you like to import one of them to MEMORY.md?',
        true
      );

      if (shouldImport) {
        const choices: string[] = [];
        if (hasClaude) choices.push('CLAUDE.md');
        if (hasAgents) choices.push('AGENTS.md');

        const sourceFile = await select(
          'Select file to import:',
          choices,
          choices[0]
        );

        info(`\nRun: ${chalk.cyan(`mem2 import --from ${sourceFile}`)}`);
      }
    }
  } else if (!hasMemory) {
    console.log();
    tip('No memory files found. Get started with:');
    console.log(chalk.dim('  - Create MEMORY.md manually, or'));
    console.log(chalk.dim('  - Import from existing CLAUDE.md or AGENTS.md'));
  }

  console.log();
  success('Initialization complete!');
  console.log();
  tip('Next steps:');
  console.log(chalk.dim('  - Edit .mem2rc.json to customize settings'));
  console.log(chalk.dim('  - Run "mem2 import --from <file>" to import existing memory'));
  console.log(chalk.dim('  - Run "mem2 compile" to generate AGENTS.md and CLAUDE.md'));
  console.log(chalk.dim('  - Run "mem2 watch" to auto-sync changes'));
  console.log();
}

/**
 * Check if file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}
